import React, { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Textarea } from './ui/textarea';
import DiscussionCard from './DiscussionCard';
import { 
  ArrowLeft, 
  Heart, 
  MessageCircle, 
  Share2, 
  Bookmark, 
  Image, 
  Video, 
  Link, 
  Smile, 
  Send, 
  Reply, 
  Award, 
  User,
  Camera,
  FileText
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ModernTopicViewProps {
  topic: any;
  replies: any[];
  user: any;
  categoryName?: string;
  recentDiscussions?: any[];
  onBack: () => void;
  onSubmitReply: (content: string) => void;
  newReplyContent: string;
  setNewReplyContent: (content: string) => void;
  onMediaUpload?: (files: File[]) => void;
  onReplyToReply?: (replyId: string, authorName: string) => void;
  onDiscussionClick?: (discussion: any) => void;
  onViewAllDiscussions?: () => void;
  onVoteTopic?: (topicId: string) => void;
  onVoteReply?: (replyId: string) => void;
  voteLoading?: string | null;
  getVoteCount?: (itemId: string, baseCount: number) => number;
}

const ModernTopicView: React.FC<ModernTopicViewProps> = ({
  topic,
  replies,
  user,
  categoryName,
  recentDiscussions = [],
  onBack,
  onSubmitReply,
  newReplyContent,
  setNewReplyContent,
  onMediaUpload,
  onReplyToReply,
  onDiscussionClick,
  onViewAllDiscussions,
  onVoteTopic,
  onVoteReply,
  voteLoading,
  getVoteCount
}) => {
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-[#121316] animate-fade-in-up">
      <div className="max-w-7xl mx-auto p-8 pt-24">
        {/* Header com navegação */}
        <div className="mb-8 animate-slide-in-left" style={{ animationDelay: '0.1s' }}>
          <div className="flex items-center gap-4 mb-4">
            <Button 
              onClick={onBack}
              variant="ghost" 
              className="group hover:bg-white/10 transition-all duration-300 text-gray-400 hover:text-white hover:scale-105"
            >
              <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform duration-300" />
              {categoryName ? `Voltar para ${categoryName}` : 'Voltar'}
            </Button>
          </div>
        </div>

        {/* Layout com duas colunas */}
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-8">
          {/* Coluna Principal - Discussão (70%) */}
          <div className="lg:col-span-4 animate-slide-in-left" style={{ animationDelay: '0.2s' }}>
            {/* Tópico Principal - Visual Moderno */}
            <div className="mb-8">
              <div className="relative bg-gradient-to-br from-gray-800/80 via-gray-900/90 to-gray-800/80 backdrop-blur-xl rounded-2xl border border-gray-700/50 shadow-2xl overflow-hidden transition-all duration-500 hover:shadow-[0_0_30px_rgba(15,255,85,0.2)]">
                <div className="absolute inset-0 bg-gradient-to-br from-[#0FFF55]/10 via-transparent to-[#0FFF55]/10"></div>
                <div className="relative">
                  {/* Header do Post */}
                  <div className="p-6 border-b border-white/10">
                    <div className="flex items-start gap-6">
                      {/* Vote Column - Estilo MasterClub */}
                      <div className="flex flex-col items-center gap-3 mr-6">
                        <button 
                          onClick={() => onVoteTopic && onVoteTopic(topic.id)}
                          disabled={voteLoading === topic.id}
                          className={`p-3 hover:bg-[#0FFF55]/10 rounded-xl transition-all duration-200 group ${
                            voteLoading === topic.id ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          {voteLoading === topic.id ? (
                            <div className="w-6 h-6 border-2 border-[#0FFF55] border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Heart className="w-6 h-6 text-gray-400 group-hover:text-[#0FFF55] transition-colors duration-200" />
                          )}
                        </button>
                        <span className="text-xl font-bold text-white">
                          {getVoteCount ? getVoteCount(topic.id, topic?.votes_count || 0) : (topic?.votes_count || 0)}
                        </span>
                      </div>

                      {/* Content */}
                      <div className="flex-1">
                        <div className="flex items-center gap-4 mb-6">
                          <div className="flex items-center gap-3">
                            <Avatar className="w-12 h-12 ring-2 ring-[#0FFF55]/50 shadow-lg">
                              <AvatarImage src={topic?.author?.avatar_url} />
                              <AvatarFallback className="bg-gradient-to-br from-[#0FFF55] to-[#0dd149] text-[#121316] font-semibold">
                                {getInitials(topic?.author?.full_name || 'U')}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex items-center gap-3">
                              <span className="text-white font-semibold text-lg">
                                {topic?.author?.full_name || 'Usuário'}
                              </span>
                              <Badge className="bg-[#0FFF55] text-[#121316] border-0 px-3 py-1">
                                <User className="w-3 h-3 mr-1" />
                                Autor
                              </Badge>
                              <span className="text-gray-400 text-sm">
                                {topic?.created_at && formatDistanceToNow(new Date(topic.created_at), {
                                  addSuffix: true,
                                  locale: ptBR
                                })}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Title */}
                        <h1 className="text-2xl font-bold text-white mb-4 leading-tight">
                          {topic?.title}
                        </h1>

                        {/* Content */}
                        <div className="prose prose-invert max-w-none mb-6">
                          <div className="text-gray-300 leading-relaxed text-base">
                            {topic?.content}
                          </div>
                        </div>

                        {/* Action Bar */}
                        <div className="flex items-center justify-between pt-4 border-t border-white/10">
                          <div className="flex items-center gap-6">
                            <button className="flex items-center gap-2 text-gray-400 hover:text-[#0FFF55] transition-colors duration-200 group">
                              <MessageCircle className="w-4 h-4" />
                              <span className="text-sm font-medium">{replies.length} comentários</span>
                            </button>
                            
                            <button className="flex items-center gap-2 text-gray-400 hover:text-[#0FFF55] transition-colors duration-200 group">
                              <Share2 className="w-4 h-4" />
                              <span className="text-sm font-medium">Compartilhar</span>
                            </button>
                            
                            <button className="flex items-center gap-2 text-gray-400 hover:text-[#0FFF55] transition-colors duration-200 group">
                              <Bookmark className="w-4 h-4" />
                              <span className="text-sm font-medium">Salvar</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Seção de Comentários */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-[#0FFF55] rounded-xl">
                    <MessageCircle className="w-6 h-6 text-[#121316]" />
                  </div>
                  <h2 className="text-xl font-bold text-white">
                    {replies.length} Comentários
                  </h2>
                </div>
              </div>

              {/* Formulário de Comentário Principal - Visual Moderno */}
              {user && (
                <div className="relative bg-gradient-to-br from-gray-800/80 via-gray-900/90 to-gray-800/80 backdrop-blur-xl rounded-2xl border border-gray-700/50 shadow-2xl p-6 mb-6">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#0FFF55]/10 via-transparent to-[#0FFF55]/10 rounded-2xl"></div>
                  <div className="relative">
                    <div className="flex items-start gap-4">
                      <Avatar className="w-10 h-10 ring-2 ring-[#0FFF55]/50 shadow-lg">
                        <AvatarImage src={user?.avatar_url} />
                        <AvatarFallback className="bg-gradient-to-br from-[#0FFF55] to-[#0dd149] text-[#121316] font-semibold">
                          {getInitials(user?.full_name || 'U')}
                        </AvatarFallback>
                      </Avatar>
                      
                      <div className="flex-1">
                        {/* Textarea Moderno - Liquid Glass */}
                        <div className="relative bg-gradient-to-br from-gray-700/60 via-gray-800/70 to-gray-700/60 backdrop-blur-xl rounded-2xl p-6 mb-4 border border-gray-600/50 shadow-2xl">
                          <div className="absolute inset-0 bg-gradient-to-br from-[#0FFF55]/5 via-transparent to-[#0FFF55]/5 rounded-2xl"></div>
                          <div className="relative">
                            <Textarea
                              placeholder="Compartilhe seus pensamentos..."
                              value={newReplyContent}
                              onChange={(e) => setNewReplyContent(e.target.value)}
                              rows={3}
                              className="resize-none border-0 bg-transparent text-white placeholder-gray-400 focus:ring-0 focus:outline-none text-base backdrop-blur-sm"
                            />
                          </div>
                        </div>
                        
                        {/* Action Bar */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <button 
                              onClick={() => {
                                const input = document.createElement('input');
                                input.type = 'file';
                                input.accept = 'image/*,video/*';
                                input.multiple = true;
                                input.onchange = (e) => {
                                  const files = Array.from((e.target as HTMLInputElement).files || []);
                                  if (onMediaUpload && files.length > 0) {
                                    onMediaUpload(files);
                                  }
                                };
                                input.click();
                              }}
                              className="p-2 hover:bg-[#0FFF55]/10 rounded-lg transition-colors duration-200 text-gray-400 hover:text-[#0FFF55]"
                              title="Adicionar mídia"
                            >
                              <Camera className="w-4 h-4" />
                            </button>
                            <button 
                              className="p-2 hover:bg-[#0FFF55]/10 rounded-lg transition-colors duration-200 text-gray-400 hover:text-[#0FFF55]"
                              title="Adicionar link"
                            >
                              <Link className="w-4 h-4" />
                            </button>
                            <button 
                              className="p-2 hover:bg-[#0FFF55]/10 rounded-lg transition-colors duration-200 text-gray-400 hover:text-[#0FFF55]"
                              title="Adicionar emoji"
                            >
                              <Smile className="w-4 h-4" />
                            </button>
                          </div>
                          
                          <Button 
                            onClick={() => onSubmitReply(newReplyContent)}
                            disabled={!newReplyContent.trim()}
                            className="bg-[#0FFF55] text-[#121316] hover:bg-[#0dd149] px-6 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-semibold"
                          >
                            <Send className="w-4 h-4 mr-2" />
                            Comentar
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Lista de Comentários */}
              {replies.length === 0 ? (
                <div className="relative bg-gradient-to-br from-gray-800/80 via-gray-900/90 to-gray-800/80 backdrop-blur-xl rounded-2xl border border-gray-700/50 shadow-2xl p-8 text-center">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#0FFF55]/10 via-transparent to-[#0FFF55]/10 rounded-2xl"></div>
                  <div className="relative">
                    <div className="w-16 h-16 bg-[#0FFF55] rounded-full flex items-center justify-center mx-auto mb-4">
                      <MessageCircle className="w-8 h-8 text-[#121316]" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      Nenhum comentário ainda
                    </h3>
                    <p className="text-gray-400 mb-4 max-w-md mx-auto text-base">
                      Seja o primeiro a comentar e iniciar a discussão!
                    </p>
                    <div className="w-16 h-1 bg-[#0FFF55] mx-auto rounded-full"></div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {replies.map((reply) => (
                    <div 
                      key={reply.id}
                      className="relative bg-gradient-to-br from-gray-800/80 via-gray-900/90 to-gray-800/80 backdrop-blur-xl rounded-2xl border border-gray-700/50 shadow-2xl overflow-hidden"
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-[#0FFF55]/10 via-transparent to-[#0FFF55]/10 rounded-2xl"></div>
                      <div className="relative">
                        {reply.is_solution && (
                          <div className="bg-[#0FFF55] px-4 py-2 flex items-center gap-2">
                            <Award className="w-4 h-4 text-[#121316]" />
                            <span className="text-[#121316] font-semibold text-sm">Solução Aceita</span>
                          </div>
                        )}
                        
                        <div className="p-6">
                          <div className="flex items-start gap-4">
                            {/* Vote Column */}
                            <div className="flex flex-col items-center gap-2 mr-4">
                              <button 
                                onClick={() => onVoteReply && onVoteReply(reply.id)}
                                disabled={voteLoading === reply.id}
                                className={`p-2 hover:bg-[#0FFF55]/10 rounded-lg transition-all duration-200 group ${
                                  voteLoading === reply.id ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                              >
                                {voteLoading === reply.id ? (
                                  <div className="w-4 h-4 border-2 border-[#0FFF55] border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <Heart className="w-4 h-4 text-gray-400 group-hover:text-[#0FFF55] transition-colors duration-200" />
                                )}
                              </button>
                              <span className="text-sm font-bold text-white">
                                {getVoteCount ? getVoteCount(reply.id, reply?.votes_count || 0) : (reply?.votes_count || 0)}
                              </span>
                            </div>

                            {/* Content */}
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-3">
                                <Avatar className="w-8 h-8 ring-2 ring-[#0FFF55]/50">
                                  <AvatarImage src={reply.author?.avatar_url} />
                                  <AvatarFallback className="bg-gradient-to-br from-[#0FFF55] to-[#0dd149] text-[#121316] text-xs font-semibold">
                                    {getInitials(reply.author?.full_name || 'U')}
                                  </AvatarFallback>
                                </Avatar>
                                
                                <div className="flex items-center gap-3">
                                  <span className="text-white font-semibold text-sm">
                                    {reply.author?.full_name || 'Usuário'}
                                  </span>
                                  <span className="text-gray-400 text-xs">
                                    {reply.created_at && formatDistanceToNow(new Date(reply.created_at), {
                                      addSuffix: true,
                                      locale: ptBR
                                    })}
                                  </span>
                                </div>
                              </div>

                              <div className="prose prose-invert max-w-none mb-4">
                                <div className="text-gray-300 leading-relaxed text-sm">
                                  {reply.content}
                                </div>
                              </div>

                              {/* Action Bar */}
                              {user && (
                                <div className="flex items-center gap-4 text-xs">
                                  <button 
                                    onClick={() => onReplyToReply && onReplyToReply(reply.id, reply.author?.full_name || 'Usuário')}
                                    className="flex items-center gap-1 text-gray-400 hover:text-[#0FFF55] transition-colors duration-200 group font-medium"
                                  >
                                    <Reply className="w-3 h-3" />
                                    <span>Responder</span>
                                  </button>
                                  
                                  <button className="flex items-center gap-1 text-gray-400 hover:text-[#0FFF55] transition-colors duration-200 group font-medium">
                                    <Share2 className="w-3 h-3" />
                                    <span>Compartilhar</span>
                                  </button>
                                  
                                  <button className="flex items-center gap-1 text-gray-400 hover:text-[#0FFF55] transition-colors duration-200 group font-medium">
                                    <Bookmark className="w-3 h-3" />
                                    <span>Salvar</span>
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Coluna Lateral - Discussões Recentes (30%) */}
          <div className="lg:col-span-2 lg:ml-4 animate-slide-in-right" style={{ animationDelay: '0.3s' }}>
            <div className="sticky top-8">
              {/* Card de Discussões Recentes */}
              <div className="relative bg-gradient-to-br from-gray-800/80 via-gray-900/90 to-gray-800/80 backdrop-blur-xl rounded-2xl border border-gray-700/50 shadow-2xl overflow-hidden transition-all duration-500 hover:shadow-[0_0_30px_rgba(15,255,85,0.2)]">
                <div className="absolute inset-0 bg-gradient-to-br from-[#0FFF55]/10 via-transparent to-[#0FFF55]/10"></div>
                <div className="relative p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-[#0FFF55] rounded-lg transition-all duration-300 hover:scale-110">
                      <MessageCircle className="w-5 h-5 text-[#121316]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">Discussões Recentes</h3>
                      <p className="text-gray-400 text-sm">Mais engajadas da comunidade</p>
                    </div>
                  </div>

                  {/* Lista de Discussões */}
                  <div className="space-y-4">
                    {recentDiscussions?.map((discussion, index) => (
                      <div 
                        key={discussion.id}
                        style={{ animationDelay: `${0.4 + index * 0.1}s` }}
                        className="animate-fade-in-up"
                      >
                        <DiscussionCard
                          discussion={discussion}
                          onClick={onDiscussionClick || (() => {})}
                          getVoteCount={getVoteCount}
                        />
                      </div>
                    ))}

                    {(!recentDiscussions || recentDiscussions.length === 0) && (
                      <div className="text-center py-8 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                        <div className="w-12 h-12 bg-[#0FFF55]/20 rounded-full flex items-center justify-center mx-auto mb-3">
                          <MessageCircle className="w-6 h-6 text-[#0FFF55]" />
                        </div>
                        <p className="text-gray-400 text-sm">Nenhuma discussão recente</p>
                      </div>
                    )}
                  </div>

                  {/* Botão Ver Todas */}
                  <div className="mt-6 pt-4 border-t border-white/10 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
                    <Button 
                      variant="ghost"
                      className="w-full text-[#0FFF55] hover:bg-[#0FFF55]/10 transition-all duration-300 hover:scale-105"
                      onClick={() => onViewAllDiscussions?.()}
                    >
                      Ver todas as discussões
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModernTopicView; 